import Axios from "axios";
import { apiUrl } from "../environment";
import { store } from "../store/store";
import { logout, loginr } from "../slice/authSlice";

function authRequestInterceptor(config) {
  const token = store.getState()?.auth?.user?.token;
  if (token) {
    config.headers.authorization = `${token}`;
  }
  config.headers.Accept = "application/json";
  return config;
}

export const axiosApi = Axios.create({
  baseURL: apiUrl,
});

axiosApi.interceptors.request.use(authRequestInterceptor);
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.authorization = `${token}`;
            return axiosApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const user = store.getState()?.auth?.user;
      const refreshToken = user?.refreshToken;

      if (refreshToken) {
        try {
          // Attempt to refresh the token
          const response = await Axios.post(`${apiUrl}/api/auth/refresh`, {
            refreshToken,
          });

          if (response.data.status) {
            const { token, refreshToken: newRefreshToken } = response.data;

            // Update user in store and localStorage
            const updatedUser = { ...user, token, refreshToken: newRefreshToken };
            store.dispatch(loginr(updatedUser));
            localStorage.setItem("users", JSON.stringify(updatedUser));

            // Update header and retry
            axiosApi.defaults.headers.authorization = `${token}`;
            originalRequest.headers.authorization = `${token}`;
            
            processQueue(null, token);
            return axiosApi(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed, logout
          localStorage.setItem("accessToken", "");
          localStorage.setItem("users", "");
          store.dispatch(logout());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    // Handle 403 or other 401 cases
    if (error.response?.status === 403 || error.response?.status === 401) {
      if (!isRefreshing) {
        localStorage.setItem("accessToken", "");
        localStorage.setItem("users", "");
        store.dispatch(logout());
      }
    }

    return Promise.reject(error);
  }
);
